/**
 * Widget-gallery seed swarm (docs/widget-gallery-page.md §3).
 *
 * One coherent demo tenant shared by all eight widget fixtures so the
 * gallery page reads as a single swarm, not eight unrelated screenshots:
 *
 *   - 2 machines: "Mac mini" (online, seat owner@demo.swarm) and
 *     "MacBook Air" (offline, seat nightshift@demo.swarm — currently
 *     seat-limited, feeding the phase-9 warn chip in swarm-status)
 *   - 3 agents: email-triage (live session, stuck on a needs-human
 *     prompt), health-sync (its overnight job failed — machine offline),
 *     morning-brief (idle, queued job held by the night budget)
 *   - the 01:30 nightly schedule, a week of token bars with the phase-9
 *     per-seat split, and a "holdReason: budget" queued job
 *
 * Timestamps are computed relative to page load so "Xm ago" copy in the
 * widgets always reads believably. Shapes MUST mirror the real tool
 * outputs in infra/lib/lambdas/mcp/server.ts (sessionView / jobView /
 * scheduleViewWithMode / the structured payloads) — not invented ones.
 */

export const minAgo = (m: number): string =>
  new Date(Date.now() - m * 60_000).toISOString();

export const hoursAgo = (h: number): string => minAgo(h * 60);

/* ── Machines (phase-9: seatId/seatLabel ride on machine rows) ────── */

export const MACHINES = {
  mini: {
    machineId: "mac-mini",
    name: "Mac mini",
    status: "online" as const,
    seatId: "seat-owner",
    seatLabel: "owner@demo.swarm",
  },
  air: {
    machineId: "macbook-air",
    name: "MacBook Air",
    status: "offline" as const,
    seatId: "seat-nightshift",
    seatLabel: "nightshift@demo.swarm",
  },
};

/* ── Agents ───────────────────────────────────────────────────────── */

export const AGENT_NAMES = ["email-triage", "health-sync", "morning-brief"];

/* ── The live session (email-triage on the Mac mini) ──────────────── */

export const LIVE_SESSION_ID = "ses-3f7a-demo";

/** The needs-human prompt the live session is stuck on. */
export const NEEDS_HUMAN_SCREEN = [
  "email-triage wants to send 3 drafted replies via Mail.app",
  "",
  "1. Yes, send them",
  "2. Yes, and don't ask again tonight",
  "3. No — leave them in drafts",
].join("\n");

/** Transcript tail in the server's "[seq] line" rendering. */
export const LIVE_SESSION_LINES = [
  "[41] assistant: 14 new messages overnight — 2 look urgent.",
  "[42] tool: Read inbox/invoice-followup.eml",
  "[43] tool: Read inbox/contract-renewal.eml",
  "[44] assistant: Drafting replies to the 2 urgent threads…",
  "[45] tool: Write drafts/re-invoice-followup.md",
  "[46] tool: Write drafts/re-contract-renewal.md",
  "[47] assistant: 3 drafts ready — asking before anything sends.",
];

/* ── Job + schedule ids (referenced across fixtures) ──────────────── */

export const JOB_IDS = {
  heldBrief: "job-brief-0042",
  queuedSweep: "job-sweep-0041",
  deliveredSweep: "job-sweep-0039",
  failedHealth: "job-health-0040",
};

export const SCHEDULE_IDS = {
  sweep: "sch-inbox-sweep",
  health: "sch-health-sync",
  brief: "sch-morning-brief",
};

/** §B2 honesty copy — mirrors USAGE_HONESTY_NOTE in mcp/server.ts. */
export const USAGE_NOTE =
  "Estimates from session telemetry — a lower bound: Claude caps count ALL " +
  "usage on the account (claude.ai chats included), and allowances are " +
  "user-entered estimates. Hard truth is the seat-limit detection when it " +
  "fires.";
